/**
 * @fileoverview Guided Filter 着色器生成模块
 * 生成WGSL着色器代码
 * 
 * @author 织
 * @version 1.0.0
 */

/**
 * 生成Guided Filter WGSL着色器代码
 * @param {GuidedFilterParams} params - 算法参数
 * @returns {string} WGSL着色器代码
 */
export function generateGuidedFilterShader(params) {
  const { sigma, epsilon, radius, blockSize } = params;
  
  return `
    // Guided Filter 参数
    struct GuidedFilterParams {
      width: u32,
      height: u32,
      sigma: f32,        // 滤波半径
      epsilon: f32,      // 正则化参数
      radius: u32        // 窗口半径
    }
    
    // 全局参数
    @group(0) @binding(2) var<uniform> params: GuidedFilterParams;
    
    // 输入纹理
    @group(0) @binding(0) var inputTexture: texture_2d<f32>;
    
    // 输出纹理
    @group(0) @binding(1) var outputTexture: texture_storage_2d<rgba32float, write>;
    
    // 共享内存缓存
    var<workgroup> sharedCache: array<vec4<f32>, 256>;
    
    /**
     * 计算局部均值
     * 使用分离高斯滤波优化性能，修复边界处理和精度问题
     * 添加权重归一化验证
     */
    fn computeLocalMean(pos: vec2<i32>) -> vec4<f32> {
      var sum = vec4<f32>(0.0);
      var weightSum = 0.0;
      
      let radius = i32(params.radius);
      let width = i32(params.width);
      let height = i32(params.height);
      
      // 预计算权重归一化因子，避免精度误差
      var normalizationFactor = 0.0;
      for (var i = -radius; i <= radius; i++) {
        normalizationFactor += exp(-f32(i * i) / (2.0 * params.sigma * params.sigma));
      }
      
      // 横向滤波 - 使用对称边界处理
      for (var x = -radius; x <= radius; x++) {
        let sampleX = clamp(pos.x + x, 0, width - 1);
        let samplePos = vec2<i32>(sampleX, pos.y);
        let weight = exp(-f32(x * x) / (2.0 * params.sigma * params.sigma)) / normalizationFactor;
        sum += textureLoad(inputTexture, samplePos, 0) * weight;
      }
      
      let horizontalResult = sum;
      
      // 纵向滤波 - 使用相同的归一化因子
      sum = vec4<f32>(0.0);
      for (var y = -radius; y <= radius; y++) {
        let sampleY = clamp(pos.y + y, 0, height - 1);
        let samplePos = vec2<i32>(pos.x, sampleY);
        let weight = exp(-f32(y * y) / (2.0 * params.sigma * params.sigma)) / normalizationFactor;
        sum += horizontalResult * weight;
      }
      
      return sum;
    }
    
    /**
     * 计算局部方差
     * 修复边界处理和精度问题，添加权重归一化验证
     */
    fn computeLocalVariance(pos: vec2<i32>, mean: vec4<f32>) -> vec4<f32> {
      var sum = vec4<f32>(0.0);
      var weightSum = 0.0;
      
      let radius = i32(params.radius);
      let width = i32(params.width);
      let height = i32(params.height);
      
      // 预计算权重归一化因子
      var normalizationFactor = 0.0;
      for (var i = -radius; i <= radius; i++) {
        normalizationFactor += exp(-f32(i * i) / (2.0 * params.sigma * params.sigma));
      }
      
      // 横向滤波 - 使用对称边界处理
      for (var x = -radius; x <= radius; x++) {
        let sampleX = clamp(pos.x + x, 0, width - 1);
        let samplePos = vec2<i32>(sampleX, pos.y);
        let weight = exp(-f32(x * x) / (2.0 * params.sigma * params.sigma)) / normalizationFactor;
        let sample = textureLoad(inputTexture, samplePos, 0);
        sum += sample * sample * weight;
      }
      
      let horizontalResult = sum;
      
      // 纵向滤波 - 使用相同的归一化因子
      sum = vec4<f32>(0.0);
      for (var y = -radius; y <= radius; y++) {
        let sampleY = clamp(pos.y + y, 0, height - 1);
        let samplePos = vec2<i32>(pos.x, sampleY);
        let weight = exp(-f32(y * y) / (2.0 * params.sigma * params.sigma)) / normalizationFactor;
        sum += horizontalResult * weight;
      }
      
      let meanSquared = sum;
      let variance = meanSquared - mean * mean;
      return max(variance, vec4<f32>(0.0));
    }
    
    /**
     * 计算局部协方差
     * 修复边界处理和精度问题，添加权重归一化验证
     */
    fn computeLocalCovariance(pos: vec2<i32>, guideMean: vec4<f32>, inputMean: vec4<f32>) -> vec4<f32> {
      var sum = vec4<f32>(0.0);
      var weightSum = 0.0;
      
      let radius = i32(params.radius);
      let width = i32(params.width);
      let height = i32(params.height);
      
      // 预计算权重归一化因子
      var normalizationFactor = 0.0;
      for (var i = -radius; i <= radius; i++) {
        normalizationFactor += exp(-f32(i * i) / (2.0 * params.sigma * params.sigma));
      }
      
      // 横向滤波 - 使用对称边界处理
      for (var x = -radius; x <= radius; x++) {
        let sampleX = clamp(pos.x + x, 0, width - 1);
        let samplePos = vec2<i32>(sampleX, pos.y);
        let weight = exp(-f32(x * x) / (2.0 * params.sigma * params.sigma)) / normalizationFactor;
        let guideSample = textureLoad(inputTexture, samplePos, 0);
        let inputSample = textureLoad(inputTexture, samplePos, 0); 
        sum += guideSample * inputSample * weight;
      }
      
      let horizontalResult = sum;
      
      // 纵向滤波 - 使用相同的归一化因子
      sum = vec4<f32>(0.0);
      for (var y = -radius; y <= radius; y++) {
        let sampleY = clamp(pos.y + y, 0, height - 1);
        let samplePos = vec2<i32>(pos.x, sampleY);
        let weight = exp(-f32(y * y) / (2.0 * params.sigma * params.sigma)) / normalizationFactor;
        sum += horizontalResult * weight;
      }
      
      let covariance = sum;
      return covariance - guideMean * inputMean;
    }
    
    /**
     * 主计算函数
     */
    @compute @workgroup_size(16, 16)
    fn main(@builtin(global_invocation_id) globalId: vec3<u32>) {
      let pos = vec2<i32>(globalId.xy);
      
      // 边界检查
      if (pos.x >= i32(params.width) || pos.y >= i32(params.height)) {
        return;
      }
      
      // 获取输入像素（作为引导图像和输入图像）
      let guide = textureLoad(inputTexture, pos, 0);
      let input = guide; // 简化：使用同一个图像作为引导和输入
      
      // 1. 计算引导图像的局部均值
      let guideMean = computeLocalMean(pos);
      
      // 2. 计算引导图像的局部方差
      let guideVariance = computeLocalVariance(pos, guideMean);
      
      // 3. 计算输入图像的局部均值
      let inputMean = guideMean; // 简化：使用相同的均值
      
      // 4. 计算协方差
      let covariance = computeLocalCovariance(pos, guideMean, inputMean);
      
      // 5. 计算线性系数
      let epsilon = params.epsilon;
      let a = covariance / (guideVariance + vec4<f32>(epsilon));
      let b = inputMean - a * guideMean;
      
      // 6. 应用Guided Filter - 生成base layer
      let baseLayer = a * guide + b;
      
      // 7. 写入base layer结果
      textureStore(outputTexture, pos, vec4<f32>(baseLayer));
    }
  `;
}

/**
 * 生成细节提取着色器代码
 * @returns {string} WGSL着色器代码
 */
export function generateDetailExtractionShader() {
  return `
    struct DetailParams {
      width: u32,
      height: u32
    }
    
    @group(0) @binding(0) var inputTexture: texture_2d<f32>;
    @group(0) @binding(1) var baseTexture: texture_2d<f32>;
    @group(0) @binding(2) var<uniform> params: DetailParams;
    @group(0) @binding(3) var detailTexture: texture_storage_2d<rgba32float, write>;
    
    @compute @workgroup_size(16, 16)
    fn main(@builtin(global_invocation_id) globalId: vec3<u32>) {
      let pos = vec2<i32>(globalId.xy);
      
      if (pos.x >= i32(params.width) || pos.y >= i32(params.height)) {
        return;
      }
      
      // 提取细节层: detail = input - base
      let input = textureLoad(inputTexture, pos, 0);
      let base = textureLoad(baseTexture, pos, 0);
      let detail = input - base;
      
      // 直接输出原始差值，不进行任何放大或归一化
      textureStore(detailTexture, pos, vec4<f32>(detail));
    }
  `;
} 